<template>
    <div>
        <StatusTrack
            :sections="trackerSections"
            :is-completed="completed === toComplete"
            :show-helper="showHelper"
            @save="$emit('saveExit')"
            @next="$emit('saveNext', 5)"
        />

        <section v-if="canFinalise" id="finalise">
            <SectionHeader
                heading="Finalise"
                :completed="completed"
                :to-complete="toComplete"
            />

            <SectionGroup
                id="review"
                heading="Review"
                :validated.sync="validated.review"
            >
                <template #default="{ validate }">
                    <p class="field--full">Please download and review the draft commissioning sheet to ensure all relevant tests have been completed and the information recorded is correct.</p>
                    <span class="arrowlink mt-md">
                        <a target="_blank" :href="`/account/job/${job}/CommissioningSheet/calculate/download`">Download</a>
                        <svg class="icon">
                            <use xlink:href="#arrow-right" />
                        </svg>
                    </span>
                    <CheckboxGroup required heading="Confirmation">
                        <CheckboxField label="I have reviewed the draft Commissioning Sheet" :value.sync="data.reviewed" @validate="validate" />
                    </CheckboxGroup>
                </template>
            </SectionGroup>

            <SectionGroup
                id="declarations"
                heading="Declarations"
                :validated.sync="validated.declarations"
            >
                <template v-if="installerName === designerName" #default="{ validate }">
                    <p class="field--full">To download the finalised commissioning sheet and documentation associated with this job the following declarations need to be completed.</p>

                    <div class="field--full mt-lg">
                        <div class="commissioning-sheet__tag small semi"><span>Installer and Designer</span></div>
                    </div>
                    <CheckboxGroup required heading="Declaration">
                        <RadioField
                            :options="[
                                `I, ${installerName} declare that I physically undertook the installation of this solar PV system.`,
                                `I, ${installerName} declare that I supervised the installation of this solar PV system and physically attended the site three times, at job set up, mid-installation, and for testing and commissioning.`,
                            ]"
                            required
                            checkbox
                            :value.sync="data.installerAttended" @validate="validate"
                        />
                        <strong class="small">AND</strong>
                        <CheckboxField :label="'I, ' + installerName + ' confirm that this system has been installed to all relevant Australian standards, state and territory regulations, manufacturer’s instructions and CEC guidelines.'" :value.sync="data.installerSystemInstallation" @validate="validate" />
                    </CheckboxGroup>
                </template>

                <template v-else #default="{ validate }">
                    <p class="field--full">To download the finalised commissioning sheet and documentation associated with this job the following declarations need to be completed.</p>

                    <div class="field--full mt-lg">
                        <div class="commissioning-sheet__tag small semi"><span>Installer</span></div>
                    </div>
                    <CheckboxGroup required heading="Declaration">
                        <RadioField
                            :options="[
                                `I, ${installerName} declare that I physically undertook the installation of this solar PV system.`,
                                `I, ${installerName} declare that I supervised the installation of this solar PV system and physically attended the site three times, at job set up, mid-installation, and for testing and commissioning.`,
                            ]"
                            required
                            checkbox
                            :value.sync="data.installerAttended" @validate="validate"
                        />
                        <strong class="small">AND</strong>
                        <CheckboxField :label="'I, ' + installerName + ' confirm that this system has been installed to all relevant Australian standards, state and territory regulations, manufacturer’s instructions and CEC guidelines.'" :value.sync="data.installerSystemInstallation" @validate="validate" />
                    </CheckboxGroup>

                    <div class="field--full mt-lg">
                        <div class="commissioning-sheet__tag small semi"><span>Designer</span></div>
                    </div>
                    <CheckboxGroup required heading="Declaration">
                        <RadioField
                            :options="[
                                `I, ${designerName} declare that I physically undertook the installation of this solar PV system.`,
                                `I, ${designerName} declare that I supervised the installation of this solar PV system and physically attended the site three times, at job set up, mid-installation, and for testing and commissioning.`,
                            ]"
                            required
                            checkbox
                            :value.sync="data.designerAttended" @validate="validate"
                        />
                        <strong class="small">AND</strong>
                        <CheckboxField :label="'I, ' + designerName + ' confirm that this system has been installed to all relevant Australian standards, state and territory regulations, manufacturer’s instructions and CEC guidelines.'" :value.sync="data.designerSystemInstallation" @validate="validate" />
                    </CheckboxGroup>
                </template>
            </SectionGroup>
        </section>

        <section v-else>
            <SectionHeader
                heading="Finalise"
                :completed="completed"
                :to-complete="toComplete"
            />
            <div class="commissioning-sheet__group">
                <p>All mandatory fields must be completed before proceeding</p>
            </div>
        </section>

        <WarningPrompt
            class="mt-lg"
            small
            text="Finalising the Commissioning Sheet will change job status to complete. Note that you will not be able to edit this job once complete. You can download and email the final version of the Commissioning Sheet once you have submitted this form."
        />

        <div class="commissioning-sheet__btn">
            <button class="btn mt-xl mr-xs" @click="$emit('saveExit')">Save and exit</button>
            <button class="btn mt-xl" :class="{'disabled' : completed !== toComplete}" @click="submit">Continue</button>
        </div>

        <ModalBox
            v-if="modalActive"
            heading="Success"
            text="You have finalised your Commissioning Sheet."
            button-text="Ok, got it"
            @clicked="finalise"
        />
    </div>
</template>
<script>
/* eslint-disable no-console */
import SectionHeader from './SectionHeader';
import SectionGroup from './SectionGroup';
import CheckboxGroup from './CheckboxGroup';
import CheckboxField from './CheckboxField';
import RadioField from './RadioField';
import WarningPrompt from '@/components/WarningPrompt';
import ModalBox from '@/components/ModalBox';
import StatusTrack from './StatusTrack';

export default {
    components: {
        SectionHeader,
        CheckboxGroup,
        CheckboxField,
        WarningPrompt,
        SectionGroup,
        StatusTrack,
        RadioField,
        ModalBox
    },

    props: {
        installerName: {
            type: String,
            required: true,
        },
        designerName: {
            type: String,
            required: true,
        },
        commissioningSheet: {
            type: Object,
            required: true,
        },
        job: {
            type: Number,
            required: true,
        },
        stepsValid: {
            type: Array,
            required: true,
        },
        showHelper: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            data: this.commissioningSheet.finalise,
            validated: {
                review: false,
                declarations: false,
            },
            toComplete: 2,
            modalActive: false,
        };
    },

    computed: {
        completed() {
            return Object.values(this.validated).filter(item => item === true).length;
        },

        trackerSections() {
            const tracker = [];
            tracker.push({
                title: 'Finalise',
                id: '#finalise',
                completed: this.completed,
                toComplete: 2,
            })

            return tracker;
        },

        canFinalise() {
            return this.stepsValid.filter(item => item === true).length == 3;
        }
    },

    mounted() {
        document.body.scrollTop = 0;
    },

    methods: {
        submit() {
            this.modalActive = true;
        },

        finalise() {
            this.modalActive = false;
            this.$emit('saveNext', 5);
        },
    }
}
</script>
